import { ArrowRight, Plus, Ruler, X } from "lucide-react";
import { Eyebrow, IMAGES, Reveal, estimateMulti, scrollToId } from "./shared";

const INCLUSIONS = [
  ["Grind 4–8″ below grade", "included"],
  ["Surface roots chasing", "quoted add-on"],
  ["Chips left as mulch", "free"],
  ["Chips raked out & hauled", "small fee"],
];

export default function Estimator({ widths, setWidths }) {
  const { low, high } = estimateMulti(widths);
  const maxW = Math.max(...widths);
  const r = 28 + (maxW / 48) * 78;
  const setW = (i) => (e) =>
    setWidths(widths.map((w, j) => (j === i ? Number(e.target.value) : w)));
  const addStump = () => setWidths((cur) => (cur.length < 5 ? [...cur, 12] : cur));
  const removeStump = (i) => setWidths((cur) => cur.filter((_, j) => j !== i));

  return (
    <section id="pricing" data-testid="pricing-estimator-section" className="scroll-mt-20 px-4 py-8 sm:px-8">
      <Reveal className="relative mx-auto max-w-7xl overflow-hidden border border-stone-800">
        <img
          src={IMAGES.chips}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-[0.14]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-[#0c0a09]/70" />

        <div className="relative grid grid-cols-1 gap-12 p-8 sm:p-12 lg:grid-cols-2">
          <div>
            <Eyebrow>02 — Simple Pricing</Eyebrow>
            <h2 className="mt-4 font-display text-2xl font-black uppercase leading-tight tracking-tight text-bone sm:text-3xl lg:text-4xl">
              Priced by the inch.<br />Not by the hour.
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-stone-400 sm:text-base">
              Grab a tape measure and go straight across the widest part of
              the stump at ground level — that means the flare roots, not
              just the trunk. Best guess works too; if it's off, I adjust the
              price once I arrive. Slide to your size and see the Friday rate
              before you ever talk to me.
            </p>

            <div className="mt-10 space-y-6">
              {widths.map((w, i) => (
                <div key={i} data-testid={`estimator-stump-row-${i}`}>
                  <div className="flex items-end justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">
                    <span className="flex items-center gap-2">
                      <Ruler className="h-3.5 w-3.5 text-amber-500" /> Stump {i + 1} width
                    </span>
                    <span className="flex items-center gap-3">
                      <span data-testid={`estimator-width-readout-${i}`} className="text-2xl font-bold tracking-tight text-amber-500">
                        {w}″
                      </span>
                      {i > 0 && (
                        <button
                          type="button"
                          data-testid={`estimator-remove-stump-${i}`}
                          onClick={() => removeStump(i)}
                          className="border border-stone-700 p-1 text-stone-500 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500"
                          aria-label={`Remove stump ${i + 1}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  </div>
                  <input
                    data-testid={`estimator-width-slider-${i}`}
                    type="range"
                    min="6"
                    max="48"
                    step="1"
                    value={w}
                    onChange={setW(i)}
                    className="stump-slider mt-3"
                    style={{ "--fill": `${((w - 6) / 42) * 100}%` }}
                    aria-label={`Stump ${i + 1} width in inches`}
                  />
                  {i === 0 && (
                    <div className="mt-1 flex justify-between font-mono text-[10px] tracking-[0.2em] text-stone-500">
                      <span>6″ sapling</span>
                      <span>48″ monster oak</span>
                    </div>
                  )}
                </div>
              ))}
              {widths.length < 5 && (
                <button
                  type="button"
                  data-testid="estimator-add-stump-btn"
                  onClick={addStump}
                  className="flex items-center gap-2 border border-dashed border-stone-700 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-400 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500"
                >
                  <Plus className="h-3.5 w-3.5" /> Add another stump
                </button>
              )}
            </div>

            <div className="mt-8 border border-amber-500/40 bg-amber-500/5 p-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone-400">
                Estimated Friday rate
              </p>
              <p data-testid="estimator-price" className="mt-2 font-display text-4xl font-black tracking-tight text-bone sm:text-5xl">
                ${low}–${high}
              </p>
              <p className="mt-2 font-mono text-[11px] leading-relaxed tracking-[0.1em] text-stone-500">
                Firm flat quote confirmed by text before any work. Extra stumps
                on the property are priced the same way — measured by the inch.
              </p>
            </div>

            <button
              data-testid="estimator-apply-btn"
              onClick={() => scrollToId("quote")}
              className="group mt-6 flex items-center gap-2 bg-amber-500 px-6 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400 active:bg-amber-600"
            >
              {widths.length > 1 ? "Apply these sizes to my quote" : "Apply this size to my quote"}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </div>

          <div className="flex flex-col justify-between gap-8">
            <div className="flex flex-col items-center justify-center border border-stone-800 bg-[#0c0a09]/60 p-6">
              <svg viewBox="0 0 300 310" className="w-full max-w-[340px]" data-testid="estimator-stump-svg">
                <path
                  d={(() => {
                    const cx = 150;
                    const cy = 140;
                    const spurs = [0.35, 1.35, 2.5, 3.65, 4.75, 5.9];
                    const pts = [];
                    const N = 96;
                    for (let i = 0; i < N; i++) {
                      const a = (i / N) * Math.PI * 2;
                      let boost = 0;
                      for (const s of spurs) {
                        const d = Math.abs(Math.atan2(Math.sin(a - s), Math.cos(a - s)));
                        if (d < 0.38) boost = Math.max(boost, (0.38 - d) / 0.38);
                      }
                      const rr = r * (1 + 0.27 * boost);
                      pts.push(`${(cx + rr * Math.cos(a)).toFixed(1)},${(cy + rr * Math.sin(a) * 0.9).toFixed(1)}`);
                    }
                    return `M${pts.join(" L")} Z`;
                  })()}
                  fill="#2b221a"
                  stroke="#f59e0b"
                  strokeWidth="3"
                  strokeLinejoin="round"
                />
                {[0.78, 0.58, 0.38, 0.2].map((f) => (
                  <ellipse key={f} cx="150" cy="140" rx={r * f} ry={r * f * 0.9} fill="none" stroke="#57534e" strokeWidth="1.5" strokeDasharray="4 5" />
                ))}
                <line x1={150 - r * 1.27} y1="272" x2={150 + r * 1.27} y2="272" stroke="#f59e0b" strokeWidth="1.5" />
                <line x1={150 - r * 1.27} y1="265" x2={150 - r * 1.27} y2="279" stroke="#f59e0b" strokeWidth="1.5" />
                <line x1={150 + r * 1.27} y1="265" x2={150 + r * 1.27} y2="279" stroke="#f59e0b" strokeWidth="1.5" />
                <text x="150" y="258" textAnchor="middle" fill="#f59e0b" fontFamily="IBM Plex Mono, monospace" fontSize="15" letterSpacing="2">
                  {`${maxW} IN`}
                </text>
                <text x="150" y="300" textAnchor="middle" fill="#78716c" fontFamily="IBM Plex Mono, monospace" fontSize="9" letterSpacing="2">
                  {widths.length > 1 ? `LARGEST OF ${widths.length} STUMPS — FLARE TO FLARE` : "MEASURE ACROSS THE FLARE ROOTS"}
                </text>
              </svg>
              <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                Widest point at ground level — flare roots included, not just the trunk
              </p>
            </div>

            <ul className="divide-y divide-stone-800 border border-stone-800 bg-charcoal/80">
              {INCLUSIONS.map(([item, tag]) => (
                <li key={item} className="flex items-center justify-between px-5 py-3.5">
                  <span className="text-sm text-stone-300">{item}</span>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${tag === "included" || tag === "free" ? "text-amber-500" : "text-stone-500"}`}>
                    {tag}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
